import { readSheet } from "@bandstand/core/chordPro";
import type { Sheet } from "@bandstand/core/chordPro";
import { observer } from "mobx-react-lite";
import styled from "styled-components";

import { EXAMPLE_SONG } from "../../exampleSong";
import { CHORD_SIZES, LINE_SPACING, SECTION_SPACING, TEXT_SIZES } from "../../stores/DisplayStore";
import type { SheetFont } from "../../stores/DisplayStore";
import type { ThemeMode } from "../../stores/ThemeStore";
import { useStores } from "../../stores/useStores";
import { ACCENTS } from "../../theme/accents";
import type { AccentName } from "../../theme/accents";
import type { ChordColor } from "../../theme/buildTheme";
import { color } from "../../theme/color";
import { SheetView } from "../sheet/SheetView";
import { Button, SmallHeading } from "../ui/controls";
import { PaneHeader, PaneTitle } from "../ui/lists";
import { PhoneBack } from "../ui/PhoneBack";
import { Segmented } from "../ui/Segmented";
import { Slider } from "../ui/Slider";
import { SettingNote, SettingsBody, SettingsGroup } from "./settingsStyles";
import { Swatches } from "./Swatches";
import type { SwatchOption } from "./Swatches";

const MODES: [ThemeMode, string][] = [
    ["system", "System"],
    ["light", "Light"],
    ["dark", "Dark"],
    ["black", "Black"],
];

const FONTS: [SheetFont, string][] = [
    ["sans", "Sans serif"],
    ["serif", "Serif"],
];

const SECTIONS: ["keep" | "split", string][] = [
    ["keep", "Whole sections"],
    ["split", "Split sections"],
];

/** The start of the example song: two sections of two lines, enough to see every setting at work. */
const PREVIEW: Sheet = (() => {
    const sheet = readSheet(EXAMPLE_SONG.text);

    return { ...sheet, sections: sheet.sections.slice(0, 2).map((section) => ({ ...section, lines: section.lines.slice(0, 2) })) };
})();

/** How the app and the chord sheets look on this device, with a preview that changes along. */
export const AppearanceSettings = observer(function AppearanceSettings() {
    const { theme, display, navigation } = useStores();
    const shade = theme.base === "light" ? "light" : "dark";
    const accents = Object.entries(ACCENTS) as [AccentName, (typeof ACCENTS)[AccentName]][];

    const accentOptions: SwatchOption<AccentName>[] = accents.map(([value, accent]) => ({
        value,
        label: accent.label,
        swatch: accent[shade].accent,
    }));

    const chordOptions: SwatchOption<ChordColor>[] = [
        { value: "accent", label: "Accent", swatch: ACCENTS[theme.accent][shade].chord },
        { value: "text", label: "Lyrics", swatch: theme.colors.text },
        ...accents.map(([value, accent]) => ({ value, label: accent.label, swatch: accent[shade].chord })),
    ];

    function reset() {
        theme.reset();
        display.reset();
    }

    return (
        <>
            <PaneHeader>
                <PhoneBack label="Back to settings" onClick={() => navigation.go({ name: "settings" })} />
                <PaneTitle>Appearance</PaneTitle>
            </PaneHeader>
            <Layout>
                <Preview aria-label="Preview">
                    <SheetView sheet={PREVIEW} lyricsOnly={false} textSize={display.textSize} splitSections={display.splitSections} />
                </Preview>
                <Settings>
                    <SettingsGroup>
                        <SmallHeading>Theme</SmallHeading>
                        <Segmented label="Theme" options={MODES} value={theme.mode} onChange={(mode) => theme.setMode(mode)} />
                        <SettingNote>
                            System follows your device between light and dark. Black is dark with a black background, for dark stages.
                        </SettingNote>
                    </SettingsGroup>
                    <SettingsGroup>
                        <SmallHeading>Accent colour</SmallHeading>
                        <Swatches
                            label="Accent colour"
                            options={accentOptions}
                            value={theme.accent}
                            onChange={(accent) => theme.setAccent(accent)}
                        />
                    </SettingsGroup>
                    <SettingsGroup>
                        <SmallHeading>Chord colour</SmallHeading>
                        <Swatches
                            label="Chord colour"
                            options={chordOptions}
                            value={theme.chords}
                            onChange={(chords) => theme.setChords(chords)}
                        />
                    </SettingsGroup>
                    <SettingsGroup>
                        <SmallHeading>Chord sheets</SmallHeading>
                        <Slider
                            label="Text size"
                            value={display.textSize}
                            min={TEXT_SIZES.smallest}
                            max={TEXT_SIZES.largest}
                            step={1}
                            format={(size) => `${size} px`}
                            onChange={(size) => display.setTextSize(size)}
                        />
                        <Slider
                            label="Chord size"
                            value={display.chordSize}
                            min={CHORD_SIZES.smallest}
                            max={CHORD_SIZES.largest}
                            step={0.05}
                            format={(size) => `${Math.round(size * 100)}%`}
                            onChange={(size) => display.setChordSize(size)}
                        />
                        <Slider
                            label="Space between lines"
                            value={display.lineSpacing}
                            min={LINE_SPACING.smallest}
                            max={LINE_SPACING.largest}
                            step={0.05}
                            format={(spacing) => `${spacing.toFixed(2)}×`}
                            onChange={(spacing) => display.setLineSpacing(spacing)}
                        />
                        <Slider
                            label="Space between sections"
                            value={display.sectionSpacing}
                            min={SECTION_SPACING.smallest}
                            max={SECTION_SPACING.largest}
                            step={0.1}
                            format={(spacing) => (spacing === 0 ? "None" : `${spacing.toFixed(1)} lines`)}
                            onChange={(spacing) => display.setSectionSpacing(spacing)}
                        />
                        <Segmented label="Font" options={FONTS} value={display.font} onChange={(font) => display.setFont(font)} />
                        <Segmented
                            label="Sections"
                            options={SECTIONS}
                            value={display.splitSections ? "split" : "keep"}
                            onChange={(value) => display.setSplitSections(value === "split")}
                        />
                        <SettingNote>
                            A section that doesn&apos;t fit at the bottom of a column moves to the next one as a whole, or is split to fill
                            the column.
                        </SettingNote>
                    </SettingsGroup>
                    <Reset onClick={reset}>Back to the standard look</Reset>
                </Settings>
            </Layout>
        </>
    );
});

/** The preview next to the settings on a wide screen, and above them on a narrow one. */
const Layout = styled.div`
    display: flex;
    flex-direction: column;

    @media (min-width: 1100px) {
        flex-direction: row-reverse;
        align-items: flex-start;
        justify-content: flex-end;
    }
`;

/** Narrower next to the preview, so the preview has room for whole lines. */
const Settings = styled(SettingsBody)`
    @media (min-width: 1100px) {
        flex: 0 0 380px;
    }
`;

const Preview = styled.section`
    margin: 8px 16px 0;
    padding: 14px 16px 2px;
    border: 1px solid ${color.border};
    border-radius: 16px;
    background: ${color.background};
    overflow: hidden;

    @media (min-width: 1100px) {
        position: sticky;
        top: 16px;
        flex: 1;
        min-width: 0;
        margin: 8px 16px 0 0;
    }
`;

const Reset = styled(Button)`
    align-self: flex-start;
`;
